import React from "react";
import { Box, Paper, Typography, Grid, Button, Chip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import useGetAirline from "../../hooks/airlines/useGetAirline";
import WrapperSection from "../../components/global/WrapperSection";
import GlobalBreadcrumbs from "../../components/global/GlobalBreadcrumbs";
import LoadingPage from "../../components/global/LoadingPage";

const AirlineDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng === "ar";

    const { airline, isLoading } = useGetAirline(id);

    if (isLoading) return <LoadingPage />;
    if (!airline) return <Typography>Not Found</Typography>;

    const isActive = airline?.status === "active";

    return (
        <WrapperSection>
            <GlobalBreadcrumbs />
            <Box sx={{ p: { xs: 2, md: 4 } }}>
                <Paper elevation={0} sx={{ p: 4, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                            {isArabic ? "تفاصيل شركة الطيران" : "Airline Details"}
                        </Typography>
                        <Button variant="outlined" onClick={() => navigate("/airlines")}>
                            {isArabic ? "عـودة" : "Back"}
                        </Button>
                    </Box>

                    <Grid container spacing={4}>
                        <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Box
                                component="img"
                                src={airline?.logo?.preview || "https://placehold.co/120x120?text=No+Logo"}
                                sx={{
                                    width: 120,
                                    height: 120,
                                    borderRadius: 2,
                                    objectFit: 'contain',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    mb: 2,
                                    bgcolor: '#fff'
                                }}
                            />
                            <Typography variant="subtitle2" color="text.secondary">
                                {isArabic ? "شعار الشركة" : "Airline Logo"}
                            </Typography>
                        </Grid>

                        <Grid item xs={12} md={8}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="caption" color="text.secondary">
                                        {isArabic ? "الاسم (عربي)" : "Name (AR)"}
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                        {airline?.name?.ar || "-"}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="caption" color="text.secondary">
                                        {isArabic ? "الاسم (إنجليزي)" : "Name (EN)"}
                                    </Typography>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                        {airline?.name?.en || "-"}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="caption" color="text.secondary">
                                        {isArabic ? "الحالة" : "Status"}
                                    </Typography>
                                    <Box sx={{ mt: 0.5 }}>
                                        <Chip
                                            label={isArabic ? (isActive ? "نشط" : "معطل") : (isActive ? "Active" : "Disabled")}
                                            color={isActive ? "success" : "default"}
                                            size="small"
                                        />
                                    </Box>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="caption" color="text.secondary">
                                        {isArabic ? "تاريخ الإضافة" : "Created At"}
                                    </Typography>
                                    <Typography variant="body1">
                                        {new Date(airline?.created_at).toLocaleDateString(isArabic ? 'ar-EG' : 'en-US')}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Paper>
            </Box>
        </WrapperSection>
    );
};

export default AirlineDetails;
